import TicketCuticle from '@/components/manage/TicketCuticle';
import TransportBadgeDetailed from '@/components/manage/tickets/badge/TransportBadgeDetailed';
import LegDate from '@/components/manage/tickets/LegDate';
import ProductDetails from '@/components/manage/tickets/ProductDetails';
import TransferTime from '@/components/manage/tickets/TransferTime';
import TripDetails from '@/components/manage/tickets/TripDetails';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, LegDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import {
  areAllAdmissionsWithStatus,
  areRefundedLike,
  areSomeAdmissionsWithStatus,
} from '@/utils/admission';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { getDurationBetweenTimestamps } from '@/utils/dateTime';
import { Badge, Icons } from '@fleet/webshop-ui';
import { type FC, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

interface LegCardProps {
  leg: LegDTO;
  nextLeg?: LegDTO;
  admissions: Array<AdmissionDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
}

const LegCard: FC<LegCardProps> = ({
  leg,
  nextLeg,
  admissions,
  passengersMap,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const allExchanged = areAllAdmissionsWithStatus(admissions, 'EXCHANGED');
  const refundedLike = areRefundedLike(admissions);

  const badgeType = useMemo(() => {
    const someRefunded = areSomeAdmissionsWithStatus(admissions, 'REFUNDED');
    const someReleased = areSomeAdmissionsWithStatus(admissions, 'RELEASED');
    const someFulfilled = areSomeAdmissionsWithStatus(admissions, 'FULFILLED');

    if (refundedLike) {
      return 'refunded';
    } else if (someRefunded && someFulfilled) {
      return 'partiallyRefunded';
    } else if (someReleased && !someRefunded) {
      return 'partiallyCancelled';
    }
    return null;
  }, [admissions, refundedLike]);

  const transferTime = nextLeg
    ? getDurationBetweenTimestamps(leg.arrivalTime, nextLeg.departureTime)
    : null;

  const renderMobile = () => {
    return (
      <div
        className={cn('flex flex-col rounded-lg bg-white text-dark', {
          'bg-neutral-pale text-neutral': refundedLike,
        })}
      >
        <div className="flex flex-row items-center justify-between rounded-t-lg border border-b-0 border-neutral-light p-3">
          <div className="flex flex-1 flex-row items-center gap-4">
            {leg.carrier?.logoUrl && (
              <img
                src={leg.carrier.logoUrl}
                alt={leg.carrier.name || 'Carrier logo'}
                className="max-w-[50%]"
              />
            )}
            <TransportBadgeDetailed leg={leg} refundedLike={refundedLike} />
          </div>
          <LegDate date={leg.departureTime} />
        </div>
        <div className="border-x border-neutral-light px-3 pb-3">
          <TripDetails leg={leg} />
        </div>
        <div className="relative">
          <TicketCuticle isVertical={false} />
          {(refundedLike ||
            badgeType === 'partiallyRefunded' ||
            badgeType === 'partiallyCancelled') && (
            <div className="absolute right-3 top-1/2 z-10 -translate-y-1/2">
              {refundedLike && (
                <Badge variant="disabled" className="mr-2 h-1/4">
                  <Icons.ticketReturn height={16} width={16} />
                  <TransText i18nKey="refunded" />
                </Badge>
              )}
              {badgeType === 'partiallyRefunded' && (
                <Badge variant="attention" className="mr-2 h-1/4">
                  <Icons.infoCircle height={16} width={16} />
                  <TransText i18nKey="partiallyRefunded" />
                </Badge>
              )}
              {badgeType === 'partiallyCancelled' && (
                <Badge variant="attention" className="mr-2 h-1/4">
                  <Icons.infoCircle height={16} width={16} />
                  <TransText i18nKey="partiallyCancelled" />
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="border border-t-0 border-neutral-light px-3 pb-3">
          <ProductDetails
            leg={leg}
            admissions={admissions}
            passengersMap={passengersMap}
          />
        </div>
      </div>
    );
  };

  const renderLaptopOrBigger = () => {
    return (
      <div
        className={cn(
          'relative my-3 flex flex-row rounded-lg bg-white text-dark laptop:flex-row',
          { 'bg-neutral-pale text-neutral': refundedLike }
        )}
      >
        <div className="flex w-1/2 flex-row justify-between rounded-l-lg border border-r-0 border-neutral-light">
          <div className="flex w-full flex-col gap-3 p-4">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-1 flex-row items-center gap-4">
                {leg.carrier?.logoUrl && (
                  <img
                    src={leg.carrier.logoUrl}
                    alt={leg.carrier.name || 'Carrier logo'}
                    className="max-w-[50%]"
                  />
                )}
                <TransportBadgeDetailed leg={leg} refundedLike={refundedLike} />
              </div>
              <LegDate date={leg.departureTime} />
            </div>
            <TripDetails leg={leg} />
          </div>
          {refundedLike && (
            <Badge variant="disabled" className="mt-3 h-1/4">
              <Icons.ticketReturn height={16} width={16} />
              <TransText i18nKey="refunded" />
            </Badge>
          )}
          {badgeType === 'partiallyRefunded' && (
            <Badge variant="attention" className="mt-3 h-1/4">
              <Icons.infoCircle height={16} width={16} />
              <TransText i18nKey="partiallyRefunded" />
            </Badge>
          )}
          {badgeType === 'partiallyCancelled' && (
            <Badge variant="attention" className="mt-3 h-1/4">
              <Icons.infoCircle height={16} width={16} />
              <TransText i18nKey="partiallyCancelled" />
            </Badge>
          )}
        </div>

        <div>
          <TicketCuticle isVertical />
        </div>

        <div className="flex w-1/2 flex-col gap-4 rounded-r-lg border border-l-0 border-neutral-light p-3 pl-2">
          <ProductDetails
            leg={leg}
            admissions={admissions}
            passengersMap={passengersMap}
          />
        </div>
      </div>
    );
  };

  if (allExchanged) {
    return null;
  }

  return (
    <div className="flex flex-col">
      {isLaptopOrBigger ? renderLaptopOrBigger() : renderMobile()}
      {Boolean(transferTime) && (
        <TransferTime
          formattedTime={transferTime}
          arrivalStation={leg.destinationStop?.name}
          departureStation={nextLeg?.originStop?.name}
        />
      )}
    </div>
  );
};

export default LegCard;
